/**
 * 指标相关的utils
 * 最新协议规定，数据源、指标、环比 这三个要素共同拼接为一个字符串提供前后端指标信息通信
 * 指标字符串的格式包含以下两种，其中dataSource、metric为动态字符串，常用数据源key、指标key替换，_ratio为固定字符串，代表环比
 * 1、dataSource__metric
 * 2、dataSource_metric_ratio
 */

import { cloneDeep } from 'lodash-es';
import { isNullOrUndefined, isEmpty, generateSortKey, analysisSortKey } from '@intelligence/v3/utils/common';
import { toActiveUsersTag, toActiveUsersTagToCN } from '@intelligence/utils/common';
import { SortType, SortMode } from '@home/enums/common';
import { LANGUAGE_CN } from '@home/_config/config';

/**
 * 数据源、指标的拼接的joiner
 */
const DATASOURCE_METRIC_JOINER = '__';

/**
 * 环比key
 */
export const RATIO = '_ratio';

/**
 * 生成指标key（入参用的）
 * @param params 指标信息参数
 * @param params.dataSource 指标的数据源
 * @param params.metric 指标
 * @param params.ratio 是否为环比，默认值为false
 * @reutrn 指标key（入参用的）
 */
export const generateMetricKey = (params: IMetric) => {
  const dataSource = params?.dataSource || '';
  const metric = params?.metric || '';
  const ratio = params?.ratio ?? false;
  const dataSourceMetric = dataSource + DATASOURCE_METRIC_JOINER + metric;
  return !ratio ? dataSourceMetric : dataSourceMetric + RATIO;
};

/**
 * 生成指标环比key（入参、取值）
 * @param key 指标key（由数据源、指标两个信息拼接而成）
 * @return 指标环比key
 */
export const generateMetricRatioKey = (key: string) => key + RATIO;

/**
 * 追加指标环比键
 * @param metricKeys 指标键列表
 */
export const appendMetricRatioKey = (metricKeys?: string[] | null): string[] => {
  if (isEmpty(metricKeys)) return [];
  const result: string[] = [];

  for (const metricKey of metricKeys!) {
    if (!metricKey) continue;
    if (metricKey.endsWith(RATIO)) continue;

    if (!result.includes(metricKey)) {
      result.push(metricKey);
    }

    const metricRatioKey = generateMetricRatioKey(metricKey);
    if (!result.includes(metricRatioKey)) {
      result.push(metricRatioKey);
    }
  }

  return result;
};

/**
 * 解析指标key（接口返回）
 * @param key 指标
 * @return 指标信息
 */
export const analysisMetricKey = (key: string): IMetric | null => {
  if (!key) return null;
  const [dataSource, metricAndRatio] = key.split(DATASOURCE_METRIC_JOINER);
  const metric = metricAndRatio?.replace(new RegExp(`${RATIO}$`), '');
  return {
    dataSource: dataSource || '',
    metric: metric || '',
    ratio: !!metricAndRatio?.endsWith(RATIO),
  };
};

/**
 * 生成排序的指标key
 * 格式为：generateMetricKey方法生成的指标key desc|asc
 * @param params 指标信息
 * @param sortType 排序方式
 */
export const generateSortMetricKey = (params: IMetric, sortType: SortType) => {
  const dataSourceMetricKey = generateMetricKey(params);
  return generateSortKey(dataSourceMetricKey, sortType);
};

export const analysisSortMetricKey = (key?: string) => {
  if (!key) return null;
  const { sortKey, sortType } = analysisSortKey(key)!;
  const metric = analysisMetricKey(sortKey)!;
  return { metric, sortType };
};

/**
 * 生成排序的指标key列表
 * @param sorts 排序状态列表
 * @return 排序的指标key列表
 */
export const generateSortMetricKeys = <T = any>(sorts: IRatioMetricSortState<T>[]): string[] => {
  if (!Array.isArray(sorts) || sorts.length === 0) return [];
  return sorts.map((sort) => {
    const dataSource = sort?.dataSource;
    const metric = sort?.metric;
    const type = sort?.type;
    const mode = sort?.mode;
    const sortMetricData: IMetric = {
      dataSource,
      metric,
      ratio: mode === SortMode.Ratio,
    };
    return generateSortMetricKey(sortMetricData, type);
  });
};

const AU_METRIC_MAPPING: IRecord<string> = {
  dau: 'au',
  wau: 'au',
  mau: 'au',
};

const GRANULARITY_AU_METRIC_MAPPING: IRecord<string> = {
  daily: 'dau',
  weekly: 'wau',
  monthly: 'mau',
};

export const getGranularityAUMetricKey = (granularity: IGranularity): string => {
  return GRANULARITY_AU_METRIC_MAPPING[granularity] || '';
};

export const getStdAUMetricKey = (key: string) => {
  return AU_METRIC_MAPPING[key] || key;
};

export const getStdAUMetricName = (locale: string, granularity: IGranularity): string => {
  const nameMap = locale === LANGUAGE_CN ? toActiveUsersTagToCN : toActiveUsersTag;
  return nameMap[granularity] || '';
};

/**
 * 获取无数据判定函数
 */
const getEmptyDataChecker = (rule?: string | ((value: any) => boolean)) => {
  // 如果未提供规则，使用默认
  if (!rule) return isEmpty;

  // 如果提供的是函数，直接使用
  if (typeof rule === 'function') return rule;

  // 如果提供的是字符串，检查预定义规则
  switch (rule) {
    // 仅undefined和null
    case 'strict':
      return isNullOrUndefined;
    // 默认规则
    case 'loose':
      return isEmpty;
    // 不包含false
    case 'no-false':
      return (value: any) =>
        isNullOrUndefined(value) ||
        (typeof value === 'string' && value.trim() === '') ||
        (typeof value === 'number' && isNaN(value));
    default:
      return isEmpty;
  }
};

/**
 * 多字段排序函数（支持字段级无数据判定）
 * @param list 要排序的数组
 * @param sorts 排序状态列表
 * @param [options] 全局配置选项
 * @param [options.getIsEmptyDataCommon] 通用无数据判定规则
 * @return 排序后的新数组
 */
export const multiRatioMetricSort = <T = any>(
  list: T[],
  sorts: IRatioMetricSortState<T>[],
  options?: {
    getIsEmptyDataCommon: IEmptyDataChecker;
  },
): T[] => {
  if (!Array.isArray(list) || list.length === 0) return [];
  // 创建数组的浅拷贝，避免修改原数组
  const sortedList = cloneDeep(list);

  // 使用sort方法进行排序
  sortedList.sort((a, b) => {
    // 遍历所有排序配置
    for (const sort of sorts) {
      const { dataSource, metric, type, mode, comparator, getIsEmptyData: getIsEmptyDataConfig } = sort || {};
      const valueKey = generateMetricKey({ dataSource, metric, ratio: mode === SortMode.Ratio });

      // 获取比较的值
      const aValue = a?.[valueKey as keyof T];
      const bValue = b?.[valueKey as keyof T];

      // 获取当前字段的空值判定函数
      const getIsEmptyData = getEmptyDataChecker(getIsEmptyDataConfig || options?.getIsEmptyDataCommon);

      // 检查是否为无数据
      const isAEmpty = getIsEmptyData(aValue);
      const isBEmpty = getIsEmptyData(bValue);

      // 处理无数据的情况（统一排在最后）
      if (isAEmpty && isBEmpty) continue;
      if (isAEmpty) return 1; // valueA排在后面
      if (isBEmpty) return -1; // valueB排在后面

      // 比较值
      const comparison = comparator?.(a, b) ?? (aValue as number) - (bValue as number);

      // 根据排序方向调整比较结果
      if (comparison !== 0) {
        return type === SortType.Asc ? comparison : -comparison;
      }
    }

    // 如果所有排序条件都相等，则保持原顺序
    return 0;
  });

  return sortedList;
};
